import { useCallback, useEffect, useMemo, useState } from 'react';
import { OptionKey } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { ROUTES } from '@/common';
import { DEFAULT_DATE_FORMAT } from '@/common/constants.ts';
import { useHistory } from '@/modules/core/hooks';
import useHealthPlanSpendTrackerStore from '@/modules/deductibles/HealthPlanDetails/stores/useHealthPlanSpendTracker.store.ts';
import useDeductiblesStore from '@/modules/deductibles/stores/useDeductibles.store.ts';

import { useDeductibles, useDependents, useGetHealthPlanYears } from './';

export const HEALTH_PLAN_YEAR_ID_QUERY_KEY = 'healthPlanYearId';

interface UseSpendTrackerLogicProps {
  currentUserId?: string;
  isIndividual?: boolean;
  defaultHealthPlanYearId?: string;
  onHealthPlanNameChange?: (healthPlanName: string) => void;
}

const useSpendTrackerLogic = ({
  currentUserId,
  isIndividual,
  defaultHealthPlanYearId,
  onHealthPlanNameChange,
}: UseSpendTrackerLogicProps) => {
  const history = useHistory();
  const userId = useDeductiblesStore((state) => state.state.userId);
  const handleSetState = useHealthPlanSpendTrackerStore((state) => state.setState);
  const [selectedHealthPlanYearId, setHealthPlanYearId] = useState<OptionKey>();

  const {
    formattedData,
    healthPlanYearOptions,
    currentHealthPlanYear,
    isLoading: isPlanYearsLoading,
  } = useGetHealthPlanYears(currentUserId);

  const selectedPlanYear = useMemo(
    () => formattedData?.find((item) => item.id == selectedHealthPlanYearId),
    [formattedData, selectedHealthPlanYearId]
  );

  const {
    chartValues,
    total,
    chartTiersValues,
    remaining,
    isLoading,
    individualTotal,
    healthPlanId,
    healthPlanName,
    coverageLevel,
    isDeductiblesLoading,
    formattedTiersData,
    isEmbeddedOnly,
  } = useDeductibles({
    focusedUserId: '',
    isIndividual,
    employeeId: currentUserId,
    serviceOn: selectedPlanYear ? dayjs(selectedPlanYear?.validTo).format(DEFAULT_DATE_FORMAT) : undefined,
    userId,
  });

  const users = useDependents({
    userId: userId || currentUserId,
    isIndividual,
  });

  const handleOpenTrackerDetails = useCallback(() => {
    history.push(ROUTES.HEALTH_PLAN_SPENDS_DETAILS(healthPlanId, selectedHealthPlanYearId as string));
  }, [history, healthPlanId, selectedHealthPlanYearId]);

  useEffect(() => {
    if (onHealthPlanNameChange && healthPlanName) {
      onHealthPlanNameChange(healthPlanName);
    }
  }, [healthPlanName, onHealthPlanNameChange]);

  useEffect(() => {
    if (defaultHealthPlanYearId) {
      setHealthPlanYearId(defaultHealthPlanYearId);
    } else if (currentHealthPlanYear?.id) {
      setHealthPlanYearId(currentHealthPlanYear?.id);
    }
  }, [currentHealthPlanYear?.id, defaultHealthPlanYearId]);

  useEffect(() => {
    if (coverageLevel) {
      handleSetState({
        healthPlanId,
        coverageLevel,
      });
    }
  }, [coverageLevel, handleSetState, healthPlanId]);

  return {
    formattedData,
    selectedHealthPlanYearId,
    setHealthPlanYearId,
    healthPlanYearOptions,
    isPlanYearsLoading,
    handleOpenTrackerDetails,
    chartValues,
    chartTiersValues,
    remaining,
    total,
    isLoading,
    users,
    individualTotal,
    coverageLevel,
    formattedTiersData,
    isDeductiblesLoading,
    isEmbeddedOnly,
  };
};

export default useSpendTrackerLogic;
